import { useMemo } from 'react';
import type { CobrancaDigitada, Parcela } from '../lib/api.ts';
import { completarReais, digitarReais, mascararDinheiro, paraApi } from '../lib/dinheiro.ts';
import { paraTela } from '../lib/datas.ts';
import { Icone } from './Icone.tsx';
import { Valor } from './Valor.tsx';
import '../estilos/dinheiro-2026.css';

/**
 * As cobranças de um contrato, linha a linha: data e valor de cada uma.
 *
 * **Um editor só.** Nasceu para dois lugares — o cadastro do contrato e a
 * correção posterior —, e desde 15/08/2026 (todo contrato nasce proposta, e a
 * cobrança nasce no aceite) serve só a "Corrigir cobranças". Continua à parte:
 * dois editores para a mesma coisa divergiriam na primeira correção.
 *
 * ## A conferência que manda
 *
 * A soma das cobranças tem de ser **exatamente** o total do contrato. Aqui ela
 * aparece o tempo todo, com o quanto falta ou sobra. Mas **quem recusa de
 * verdade é o servidor**: a tela adianta a conversa, não é a garantia.
 *
 * ## Parcela já baixada não se edita
 *
 * Aparece travada, com o dia do pagamento. O que já entrou no caixa é
 * história: reescrevê-la faria o Financeiro contar uma coisa diferente da que
 * aconteceu. Para mexer, tira-se a baixa antes — ato consciente e registrado.
 *
 * ## Convenção deste arquivo
 *
 * `valor` aqui é **sempre o texto mascarado** que aparece no campo
 * (`1.666,68`), nunca o formato da API. As duas conversões vivem em
 * `paraLinhas` e `paraCobrancas`, embaixo — um lugar só, para não haver dúvida
 * de quem está em qual formato.
 */

export type LinhaDeCobranca = {
  id?: string;
  numero: number;
  /** Mascarado: `1.666,68`. Ver a convenção acima. */
  valor: string;
  vencimentoEm: string;
  observacao?: string | null;
  /** Presente quando já foi baixada. Trava a linha. */
  pagoEm?: string | null;
  emDiaNaoUtil?: boolean;
  feriado?: string | null;
};

/** Do que veio do servidor para o que o editor usa. */
export function paraLinhas(parcelas: Parcela[]): LinhaDeCobranca[] {
  return parcelas.map((p) => ({
    id: p.id,
    numero: p.numero,
    // `valor` vem nulo quando quem olha é colaborador — mas este editor é do
    // master, e contrato inteiro também. Se um dia chegar nulo aqui, é defeito
    // de rota, não caso a tratar: some, e aparece.
    valor: mascararDinheiro(((p.valor ?? '0') + '').replace('.', '')),
    vencimentoEm: p.vencimentoEm,
    observacao: p.observacao,
    pagoEm: p.pagoEm,
    emDiaNaoUtil: p.emDiaNaoUtil,
    feriado: p.feriado,
  }));
}

/** Do que o editor usa para o que a API espera. */
export function paraCobrancas(linhas: LinhaDeCobranca[]): CobrancaDigitada[] {
  return linhas.map((l) => ({
    ...(l.id ? { id: l.id } : {}),
    numero: l.numero,
    valor: paraApi(l.valor),
    vencimentoEm: l.vencimentoEm,
    observacao: l.observacao ?? null,
  }));
}

/** Em centavos, para a soma não passar perto de ponto flutuante. */
function centavos(mascarado: string): number {
  const digitos = mascarado.replace(/\D/g, '');
  return digitos ? Number(digitos) : 0;
}

export function EditorDeCobrancas({
  linhas,
  totalMascarado,
  aoMudar,
}: {
  linhas: LinhaDeCobranca[];
  /** O total do contrato, mascarado. Vazio enquanto não foi digitado. */
  totalMascarado: string;
  aoMudar: (linhas: LinhaDeCobranca[]) => void;
}) {
  const soma = useMemo(() => linhas.reduce((s, l) => s + centavos(l.valor), 0), [linhas]);
  const total = centavos(totalMascarado);
  const diferenca = total - soma;
  const emReais = (c: number) => (c / 100).toFixed(2);

  const trocarData = (indice: number, data: string) =>
    aoMudar(linhas.map((l, i) => (i === indice ? { ...l, vencimentoEm: data } : l)));

  const trocarValor = (indice: number, bruto: string) =>
    aoMudar(
      linhas.map((l, i) => (i === indice ? { ...l, valor: digitarReais(bruto) } : l)),
    );

  const remover = (indice: number) => {
    // Renumera as que sobraram: buraco na numeração faz a pessoa procurar uma
    // "parcela 3" que não existe. A entrada (número 0) mantém o lugar dela.
    const restantes = linhas.filter((_, i) => i !== indice);
    let proximo = 1;
    aoMudar(restantes.map((l) => (l.numero === 0 ? l : { ...l, numero: proximo++ })));
  };

  const acrescentar = () => {
    const ultima = linhas[linhas.length - 1];
    aoMudar([
      ...linhas,
      {
        numero: Math.max(0, ...linhas.map((l) => l.numero)) + 1,
        // Nasce com a diferença que falta, quando falta: é quase sempre o
        // valor certo, e corrigir é mais rápido que digitar do zero.
        valor: diferenca > 0 ? mascararDinheiro(String(diferenca)) : '',
        vencimentoEm: ultima ? umMesDepois(ultima.vencimentoEm) : '',
      },
    ]);
  };

  /* ⭐ No desenho novo desde 18/09/2026 (a tabela do passo a passo, `imv-`):
     muda a aparência, não a conta — a soma, a trava da paga e o renumerar são os mesmos. */
  return (
    <div className="cobrancas">
      <div className="imv-tabela din-tabela" role="table" aria-label="Cobranças">
        <div className="imv-tl imv-tl-cab din-grade-cobranca" role="row">
          <span role="columnheader">Cobrança</span>
          <span role="columnheader">Vencimento</span>
          <span role="columnheader">Valor</span>
          <span role="columnheader">
            <span className="apenas-leitor">Ações</span>
          </span>
        </div>
        {linhas.map((l, i) => {
          const travada = Boolean(l.pagoEm);
          return (
            <div key={l.id ?? `nova-${i}`} className={`imv-tl din-grade-cobranca${travada ? ' din-paga' : ''}`} role="row">
              <span role="cell" className="din-rotulo">
                {l.numero === 0 ? 'Entrada' : `Parcela ${l.numero}`}
              </span>
              <span role="cell">
                {travada ? (
                  <span className="mono">{paraTela(l.vencimentoEm)}</span>
                ) : (
                  <input
                    type="date"
                    className="mono"
                    value={l.vencimentoEm}
                    onChange={(e) => trocarData(i, e.target.value)}
                    aria-label={`Vencimento da cobrança ${l.numero}`}
                  />
                )}
                {l.emDiaNaoUtil && <small className="din-aviso">cai em {l.feriado ?? 'fim de semana'}</small>}
              </span>
              <span role="cell">
                {/* Travada mostra pelo componente Valor, que obedece ao olho
                    do cabeçalho. Campo aberto não passa por ele: não se
                    digita dentro do que está escondido. */}
                {travada ? (
                  <span className="mono">
                    <Valor de={paraApi(l.valor)} />
                  </span>
                ) : (
                  <input
                    className="mono"
                    inputMode="decimal"
                    value={l.valor}
                    onChange={(e) => trocarValor(i, e.target.value)}
                    onBlur={(e) => aoMudar(linhas.map((x, j) => (j === i ? { ...x, valor: completarReais(e.target.value) } : x)))}
                    aria-label={`Valor da cobrança ${l.numero}`}
                  />
                )}
              </span>
              <span role="cell" className="din-acao">
                {travada ? (
                  <span className="din-pago">pago {paraTela(l.pagoEm!)}</span>
                ) : (
                  linhas.length > 1 && (
                    <button type="button" className="imv-x" aria-label={`Remover a cobrança ${l.numero}`} title="Remover" onClick={() => remover(i)}>
                      <Icone nome="lixeira" tamanho={14} />
                    </button>
                  )
                )}
              </span>
            </div>
          );
        })}
      </div>

      <div className="din-rodape">
        <button type="button" className="imv-mais" onClick={acrescentar}>
          <Icone nome="mais" tamanho={14} /> Acrescentar cobrança
        </button>
        <p className={'din-soma' + (diferenca === 0 ? ' fecha' : '')}>
          {diferenca === 0 ? (
            <>
              Somam <Valor de={emReais(soma)} /> — fecha com o contrato.
            </>
          ) : (
            <>
              Somam <Valor de={emReais(soma)} /> de <Valor de={emReais(total)} /> · {diferenca > 0 ? 'faltam' : 'sobram'} <Valor de={emReais(Math.abs(diferenca))} />
            </>
          )}
        </p>
      </div>
    </div>
  );
}

/**
 * Um mês depois, preservando o dia — a mesma regra do servidor.
 *
 * Dia que não existe no mês seguinte vira o último dia dele: 31/01 + 1 mês é
 * 28/02, e não 03/03 como o JavaScript faria sozinho.
 */
function umMesDepois(data: string): string {
  if (!data) return '';
  const d = new Date(`${data}T00:00:00.000Z`);
  const dia = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + 1);
  const ultimo = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(dia, ultimo));
  return d.toISOString().slice(0, 10);
}
